/**
 * Chatten på en programpost (Atle, 2. september).
 *
 * > «Legg til en egen side helt til høyre i stien. Dette skal være en chat for alle som er
 * > involvert kan være med i. Chaten skal være god og lik som teams. Mulighet for emojis, bilder
 * > vedlegg og slikt. Har man en ulest chatn er det markert i menyen.»
 * > «kommer det en ny ulest chat kommer denne i inboksen din, ved å trykke på den i inboks åpner
 * > man chaten.»
 *
 * ---------------------------------------------------------------------------------------------
 * THIS IS NOT A SECOND MESSAGING SYSTEM. IT IS THE INBOX, ROOTED AT AN ACT.
 *
 * A performance chat IS a `threads` row with `linked_object_type = 'performance'`. Every field the
 * conversation needs already exists — the author, the body, `sent_at`, `edited_at`, the soft
 * delete, the `attachments` rows hanging off `ownerType: 'message'` — and unread is
 * `thread_participants.last_message_at > last_read_at`, the same two columns the Inbox pages off.
 * So this contract reuses `MessageDto` verbatim rather than declaring a parallel shape, and the
 * chat turns up in the Inbox for free because the Inbox lists `thread_participants`.
 *
 * The one thing that is genuinely new is the ADDRESS: every route here names an act rather than a
 * thread, because the client holds a performance id and must not have to learn a thread id (or
 * discover that there is not one yet) before it can draw a screen.
 *
 * ---------------------------------------------------------------------------------------------
 * THE THREAD IS CREATED BY THE FIRST MESSAGE, NEVER BY A READ.
 *
 * `GET /v1/performance-chat` answers `threadId: null` for an act nobody has written on, and that
 * is a real state rather than a missing one: it is what an empty chat IS. A GET that created the
 * thread would be a read with a side effect — the pattern `PerformanceAdvanceService.applyTemplate`
 * is explicit about refusing — and it would litter the database with a thread per act anybody ever
 * clicked. Migration 0053's partial unique index is what makes the create-on-first-write safe when
 * two people press send at the same moment.
 *
 * ---------------------------------------------------------------------------------------------
 * WHO IS IN IT, AND WHAT PARTICIPATION IS FOR HERE.
 *
 * Reading and writing are gated on the ACT — `performance:read` and `performance:update` — so
 * «alle som er involvert» is answered by the same cells that open the booking, not by a
 * membership list somebody has to maintain per act. Participation therefore is NOT the access
 * rule on this page (it is on the Inbox, and RLS still enforces it for `limited` and `external`,
 * who cannot see a thread they are not in — for them the chat opens the moment they are let into
 * it by writing, or by an object grant plus a first post).
 *
 * What participation IS for is the reader's own state: `last_read_at` is where the unread marker
 * comes from, and the participant row is what puts the chat in somebody's Inbox. So a row is
 * written the moment somebody ACTS on the chat — posts, or marks it read — and never before. Two
 * consequences, both deliberate:
 *
 * * `hasUnread` for somebody with no participant row is «there are messages and I have never
 *   opened this», which is what a chat you have not seen should say on the tab.
 * * The chat reaches an Inbox once its owner has engaged with it once. Whether it should instead
 *   be pushed to everybody on the act's People tab before that is a product question, not a
 *   mechanical one — see the report.
 *
 * ---------------------------------------------------------------------------------------------
 * MENTIONS, SVAR OG REDIGERING KOM 2. SEPTEMBER, og notatet som sto her sa det motsatte.
 *
 * Atle: «Ingen funksjon i chat for å redigere en melding, eller svare på en melding som i teams.
 * Legg dette til i alle chatter. Heller ingen funksjon for å nevne noen med @. Altså her må du
 * legge til hva en helt normal chat har i teams. Nå gir du meg bare det minimume.»
 *
 * Det gamle argumentet mot `@` var at en omtale bare får nevne en DELTAKER, og at deltakelse på
 * denne siden var tynn med vilje — så plukkeren ville vært tom. Det holdt ikke lenger etter sak
 * 73: den første meldingen melder inn alle som har fått chaten delt med seg og den som opprettet
 * bookingen, så listen er ekte fra første melding. Regelen står: en omtale kan bare nevne noen som
 * ER i samtalen, fordi det motsatte er å sende noen en lenke til noe de ikke kan åpne.
 *
 * SVAR ER `in_reply_to_message_id`, som har ligget i `messages` siden første dag og aldri vært
 * brukt. Et svar er ikke en undertråd: Teams tegner det som et sitat over meldingen, samtalen
 * fortsetter i én strøm, og det er den formen som overlever at tre personer svarer på hver sin
 * melding samtidig.
 *
 * REDIGERING ER `edited_at`, som også fantes. Bare forfatteren, og alltid synlig — «(endret)» ved
 * siden av klokkeslettet — fordi en samtale hvor tidligere ord kan byttes ut usynlig er en samtale
 * ingen kan sitere.
 */
import { z } from 'zod';

import { cursorQuerySchema, page, sortDirectionSchema } from '../http/pagination.js';
import { enquiryAvailabilitySchema } from './booking-enquiries.js';
import { messageDtoSchema, threadParticipantDtoSchema } from './inbox.js';

/**
 * The one sort a chat has, and the same one a thread's messages have: when it was sent, off
 * `ix_messages__org_thread_sent`. Read backwards for `desc` and forwards for `asc`, with no sort
 * node either way, because `sent_at` is written once and never moves.
 */
export const PERFORMANCE_CHAT_SORT_KEYS = ['sent'] as const;
export type PerformanceChatSortKey = (typeof PERFORMANCE_CHAT_SORT_KEYS)[number];

/** The same ceiling `createMessageRequestSchema` sets. A chat message is a message. */
const bodySchema = z.string().trim().min(1).max(20_000);

/**
 * How many of the people in the chat the state response names before `participantCount` speaks
 * for the rest. The panel draws faces, not a register.
 *
 * 40 og ikke 12 fra QA-runde 3 punkt 11: lista er nå gruppert per hus og foldet sammen, og tallene
 * per hus («Standing Ovation 3 · God Nok 4») regnes av den. Med tolv ville et hus som kom sist i
 * alfabetet forsvunnet fra oppsummeringen i en stor chat.
 */
export const PERFORMANCE_CHAT_PARTICIPANT_PREVIEW = 40;

/**
 * The chat as it stands for THIS reader — one request, answered whether or not a thread exists.
 *
 * `threadId` is null for an act nobody has written on. Everything else then reads as the empty
 * chat it is: no messages, nothing unread, nobody in it. A client draws the same panel either
 * way and the composer is the only control that matters.
 */
/**
 * Én deltaker og hvor langt hen har lest (sak 81.2).
 *
 * `lastReadAt` er nullbar: en som er lagt til i tråden men aldri har åpnet den har ikke lest noe,
 * og det er en annen tilstand enn «har lest alt for lenge siden». Klienten tegner dem ikke.
 */
export const chatReaderSchema = z
  .object({
    personId: z.uuid(),
    displayName: z.string().nullable(),
    lastReadAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'ChatReader' });
export type ChatReader = z.infer<typeof chatReaderSchema>;

export const performanceChatDtoSchema = z
  .object({
    performanceId: z.uuid(),
    /** Null until the first message creates it. Also what the Inbox row points at. */
    threadId: z.uuid().nullable(),
    messageCount: z.number().int().nonnegative(),
    /** Null for an empty chat; otherwise the thread's trigger-maintained activity stamp. */
    lastMessageAt: z.iso.datetime().nullable(),
    /**
     * «Har man en ulest chatn er det markert i menyen.»
     *
     * For a participant this is `last_message_at > last_read_at`, exactly as in the Inbox. For
     * somebody with no participant row it is «there is something here and I have never opened
     * it» — which is the honest answer, and the one that makes a brand-new chat visible on the
     * tab of everybody who may open the act.
     */
    hasUnread: z.boolean(),
    /** Null until the reader has opened the chat once. */
    lastReadAt: z.iso.datetime().nullable(),
    participantCount: z.number().int().nonnegative(),
    /**
     * Who has taken part, capped at `PERFORMANCE_CHAT_PARTICIPANT_PREVIEW`. INCLUDES the caller,
     * unlike an Inbox row: this panel is about the conversation rather than about who you are
     * talking to, so leaving yourself out would make the count disagree with the faces.
     */
    participants: z.array(threadParticipantDtoSchema),
    /**
     * «Lest av» — hvor langt hver deltaker har lest (sak 81.2, Atle 3. september).
     *
     * ---------------------------------------------------------------------------------------------
     * ÉN LISTE PÅ TILSTANDEN, IKKE ET FELT PER MELDING.
     *
     * Klienten tegner «Lest av Kari, Ola» under den SISTE meldingen, og regner det ut ved å
     * sammenligne hver leser sitt `lastReadAt` med meldingens `sentAt`. Å sende «hvem har lest
     * denne» på hver melding ville vært det samme svaret gjentatt tjue ganger, og det ville blitt
     * feil i det øyeblikket noen leste mens sida sto åpen.
     *
     * DEN SOM LESER SELV ER IKKE MED. «Lest av deg» er en opplysning ingen trenger, og den ville
     * stått under hver eneste melding man selv har sett.
     *
     * INGEN LAGRING KOM TIL: `thread_participants.last_read_at` har eksistert siden fase 1 og
     * vedlikeholdes av `app.bump_thread_activity`. Dette er en VISNING av noe som allerede var
     * sant — det er derfor punktet var billig nok til å bli med.
     */
    readers: z.array(chatReaderSchema),
    /** Whether the reader may post. The API refuses again; this is what hides the composer. */
    canPost: z.boolean(),
  })
  .meta({ id: 'PerformanceChat' });

/**
 * A page of the conversation, newest first by default — so the first request always brings the
 * part somebody opened the chat for, and «vis eldre» is the next page rather than the first.
 */
export const listPerformanceChatMessagesQuerySchema = cursorQuerySchema(PERFORMANCE_CHAT_SORT_KEYS)
  .extend({
    performanceId: z.uuid(),
    direction: sortDirectionSchema.default('desc'),
    /**
     * Søk i chatten (sak 81.4, Atle 3. september).
     *
     * ---------------------------------------------------------------------------------------------
     * ET FILTER PÅ DEN SAMME LISTA, IKKE EN EGEN RUTE.
     *
     * Et søk i en samtale er den samme lista med færre rader i, og den skal pagineres, sorteres og
     * tegnes på nøyaktig samme måte. En egen `/search`-rute ville vært en andre spørring med sin
     * egen markør, sin egen rekkefølge og sin egen måte å bli feil på.
     *
     * DELSTRENG OG IKKE FULLTEKST. En produksjonschat er kort, og det man leter etter er et
     * flynummer, et romnummer eller et navn — «SK4108», «301», «Kari». Fulltekstsøk ville stemmet
     * ordene og dermed ikke funnet «SK41» i «SK4108», som er nøyaktig slik noen søker.
     */
    q: z.string().trim().min(1).max(200).optional(),
  })
  .meta({ id: 'ListPerformanceChatMessagesQuery' });

export const performanceChatMessageListResponseSchema = page(
  messageDtoSchema,
  'PerformanceChatMessageListResponse',
);

/** How many people one message may name. A sentence, not a distribution list. */
export const MAX_CHAT_MENTIONS = 20;

/**
 * Posting. `performanceId` rather than a thread id, because the thread may not exist yet and the
 * client must not have to care: this request is «say this on this act», and creating the
 * conversation is the server's business.
 *
 * No `source` and no `bodyFormat`: everything this API writes is internal markdown, and the email
 * fields are Phase 3.
 */
export const createPerformanceChatMessageRequestSchema = z
  .object({
    performanceId: z.uuid(),
    body: bodySchema,
    /**
     * Hvilken melding dette er et svar på. Må ligge i den samme samtalen — serveren sjekker det,
     * og et svar på en melding fra en annen booking er en 400 framfor et sitat ingen kan følge.
     */
    inReplyToMessageId: z.uuid().nullish(),
    /**
     * Hvem komponisten mente å nevne. Serveren skjærer den mot deltakerne og skriver snittet —
     * se `InboxService.insertMessage`, hvor den samme regelen står med sin begrunnelse.
     */
    mentionPersonIds: z.array(z.uuid()).max(MAX_CHAT_MENTIONS).optional(),
  })
  .meta({ id: 'CreatePerformanceChatMessageRequest' });

/**
 * Å rette en melding man selv har skrevet.
 *
 * BARE BRØDTEKSTEN. Et svar peker på en melding og en omtale navngir en person, og begge to er
 * fakta om øyeblikket meldingen ble sendt — å la dem flyttes etterpå ville betydd at et varsel
 * kunne komme til noen for noe som ikke lenger står der, eller at et sitat kunne bytte hvem det
 * siterer. Angrer man på hvem man svarte, skriver man en ny melding.
 */
export const updatePerformanceChatMessageRequestSchema = z
  .object({ body: bodySchema })
  .meta({ id: 'UpdatePerformanceChatMessageRequest' });

/**
 * Hvem `@` kan nevne i denne chatten.
 *
 * Egen rute framfor et felt på tilstanden, av samme grunn som antallet uleste i varsler er det:
 * det er et oppslag som skjer når noen skriver `@`, ikke noe hver visning av siden skal betale
 * for. `q` er det som er skrevet etter krøllalfaen.
 */
export const listPerformanceChatMentionsQuerySchema = z
  .object({
    performanceId: z.uuid(),
    q: z.string().trim().max(80).optional(),
  })
  .meta({ id: 'ListPerformanceChatMentionsQuery' });

export const performanceChatMentionListResponseSchema = z
  .object({ items: z.array(threadParticipantDtoSchema) })
  .meta({ id: 'PerformanceChatMentionListResponse' });

/**
 * «Jeg har lest dette» — the write half of the unread marker, and the one place `last_read_at`
 * moves. It also ENROLS the caller: a person who has read the chat once is somebody the next
 * message concerns, so the participant row is written here if it is missing.
 *
 * `upToMessageId` is what the client actually rendered. Omitted means «everything that exists
 * right now», which is the deliberate mark-all-read gesture. The marker never moves backwards
 * whatever this says.
 */
export const markPerformanceChatReadRequestSchema = z
  .object({
    performanceId: z.uuid(),
    upToMessageId: z.uuid().optional(),
  })
  .meta({ id: 'MarkPerformanceChatReadRequest' });

export type PerformanceChatDto = z.infer<typeof performanceChatDtoSchema>;
export type ListPerformanceChatMessagesQuery = z.infer<
  typeof listPerformanceChatMessagesQuerySchema
>;
export type PerformanceChatMessageListResponse = z.infer<
  typeof performanceChatMessageListResponseSchema
>;
export type CreatePerformanceChatMessageRequest = z.infer<
  typeof createPerformanceChatMessageRequestSchema
>;
export type MarkPerformanceChatReadRequest = z.infer<typeof markPerformanceChatReadRequestSchema>;
export type UpdatePerformanceChatMessageRequest = z.infer<
  typeof updatePerformanceChatMessageRequestSchema
>;
export type ListPerformanceChatMentionsQuery = z.infer<
  typeof listPerformanceChatMentionsQuerySchema
>;
export type PerformanceChatMentionListResponse = z.infer<
  typeof performanceChatMentionListResponseSchema
>;

/* ============================================================================================
 * BOOKINGCHATTEN — den samme motoren, et annet emne.
 *
 * Atle, 8. september: «Her skal du bruke alle funksjoner som du har gjort tidligere chatter.»
 *
 * Formene under er programpostens med ETT felt byttet: `bookingArtistId` framfor `performanceId`.
 * At de likevel står hver for seg, framfor en delt form med et `objectId`, er en beslutning:
 * DTO-en er kontrakten en klient leser, og et generisk id-felt ville tvunget hver klient til å
 * vite hva slags objekt den holder. Meldingene, omtalene, reaksjonene og vedleggene deler derimot
 * form fullt ut — en melding er en melding.
 * ============================================================================================ */

export const bookingChatDtoSchema = performanceChatDtoSchema
  .omit({ performanceId: true })
  .extend({ bookingArtistId: z.uuid() })
  .meta({ id: 'BookingChatDto' });

export const listBookingChatMessagesQuerySchema = listPerformanceChatMessagesQuerySchema
  .omit({ performanceId: true })
  .extend({ bookingArtistId: z.uuid() })
  .meta({ id: 'ListBookingChatMessagesQuery' });

export const createBookingChatMessageRequestSchema = createPerformanceChatMessageRequestSchema
  .omit({ performanceId: true })
  .extend({ bookingArtistId: z.uuid() })
  .meta({ id: 'CreateBookingChatMessageRequest' });

export const markBookingChatReadRequestSchema = markPerformanceChatReadRequestSchema
  .omit({ performanceId: true })
  .extend({ bookingArtistId: z.uuid() })
  .meta({ id: 'MarkBookingChatReadRequest' });

export type BookingChatDto = z.infer<typeof bookingChatDtoSchema>;
export type ListBookingChatMessagesQuery = z.infer<typeof listBookingChatMessagesQuerySchema>;
export type CreateBookingChatMessageRequest = z.infer<
  typeof createBookingChatMessageRequestSchema
>;
export type MarkBookingChatReadRequest = z.infer<typeof markBookingChatReadRequestSchema>;

/* ============================================================================================
 * SAMTALEN OM ETT ENGASJEMENT (migrering 0099/0100).
 *
 * Atle, 9. september: «Hvorfor har du plassert en chat mot en arrangør inne i oversikten til
 * artisten, og ikke på arrangementet artisten skal spille der, eller arrangementet der
 * forespørselen/budet ligger?»
 *
 * ETT ENGASJEMENT ER ÉN KVELD: God Nok som spør om 16. juli er en annen forhandling enn Nordlys
 * som spør om 22. januar, med hver sin pris, frist og svar. Samtalen hører derfor til den ene
 * kvelden — og den flytter med når utkastet blir programpost, fordi salget og produksjonen er den
 * samme avtalen sett fra hver sin ende.
 *
 * ÉN ID, OG HUSET AVGJØR HVA DEN ER. En festival kjenner sitt BUD, et byrå kjenner sin
 * HENVENDELSE, og ingen av dem skal kjenne den andres id. `engagementId` er derfor den id-en huset
 * selv har, og serveren slår den opp i sitt eget hus — en id som ikke er husets er en 404, som
 * overalt ellers. Alternativet, to endepunkter som gjør det samme, ville betydd to kontrakter, to
 * sett nøkler og to klienter for én samtale.
 * ============================================================================================ */

export const engagementChatDtoSchema = performanceChatDtoSchema
  .omit({ performanceId: true })
  .extend({
    engagementId: z.uuid(),
    /** `bid` i et festivalhus, `enquiry` i et byråhus — det skjermen trenger for å si hva den ser. */
    engagementKind: z.enum(['bid', 'enquiry']),
    /** Motparten samtalen går med, når de finnes i Tvitvi. Null gjør dette til en intern tråd. */
    counterpartyName: z.string().nullable(),
  })
  .meta({ id: 'EngagementChatDto' });

export const listEngagementChatMessagesQuerySchema = listPerformanceChatMessagesQuerySchema
  .omit({ performanceId: true })
  .extend({ engagementId: z.uuid() })
  .meta({ id: 'ListEngagementChatMessagesQuery' });

export const createEngagementChatMessageRequestSchema = createPerformanceChatMessageRequestSchema
  .omit({ performanceId: true })
  .extend({ engagementId: z.uuid() })
  .meta({ id: 'CreateEngagementChatMessageRequest' });

export const markEngagementChatReadRequestSchema = markPerformanceChatReadRequestSchema
  .omit({ performanceId: true })
  .extend({ engagementId: z.uuid() })
  .meta({ id: 'MarkEngagementChatReadRequest' });

export type EngagementChatDto = z.infer<typeof engagementChatDtoSchema>;
export type ListEngagementChatMessagesQuery = z.infer<
  typeof listEngagementChatMessagesQuerySchema
>;
export type CreateEngagementChatMessageRequest = z.infer<
  typeof createEngagementChatMessageRequestSchema
>;
export type MarkEngagementChatReadRequest = z.infer<typeof markEngagementChatReadRequestSchema>;

/* ============================================================================================
 * BYRÅETS INTERNE ROM OM EN FORESPØRSEL (QA-runde 3 punkt 10, migrering 0368/0369).
 *
 * Atle, sak 211.3: agenten må kunne drøfte forespørselen med artistens management før svaret går
 * til arrangøren. Samme motor, et eget rom: byrået eier tråden, arrangøren er aldri med, og
 * management i et annet hus slipper inn per person gjennom sitt eget sete.
 *
 * ADRESSERT MED FORESPØRSELENS ID, som engasjementschatten, og slått opp i BYRÅETS hus. En festival
 * som prøver den samme id-en får 404 — den finnes ikke i deres register, og svaret røper ikke at et
 * rom finnes.
 * ============================================================================================ */

/**
 * EN GJEST BYRÅET HAR VALGT INN I ROMMET (288.1, 1155): en ekstern med levende artistdeling. Hen
 * leser fra `addedAt`. Lista er byråets; gjesten selv får den tom.
 */
export const internalChatGuestDtoSchema = z
  .object({
    personId: z.uuid(),
    displayName: z.string(),
    addedAt: z.iso.datetime(),
  })
  .meta({ id: 'InternalChatGuest' });

export const internalChatDtoSchema = performanceChatDtoSchema
  .omit({ performanceId: true })
  .extend({
    enquiryId: z.uuid(),
    /** Om leseren får legge personer til. Samme celle som å skrive; API-et spør igjen. */
    canAddParticipants: z.boolean(),
    /** 288.1: gjestene byrået har valgt inn, med tidspunktet de leser fra. */
    guests: z.array(internalChatGuestDtoSchema),
  })
  .meta({ id: 'InternalChatDto' });

export const listInternalChatMessagesQuerySchema = listPerformanceChatMessagesQuerySchema
  .omit({ performanceId: true })
  .extend({ enquiryId: z.uuid() })
  .meta({ id: 'ListInternalChatMessagesQuery' });

export const createInternalChatMessageRequestSchema = createPerformanceChatMessageRequestSchema
  .omit({ performanceId: true })
  .extend({ enquiryId: z.uuid() })
  .meta({ id: 'CreateInternalChatMessageRequest' });

export const markInternalChatReadRequestSchema = markPerformanceChatReadRequestSchema
  .omit({ performanceId: true })
  .extend({ enquiryId: z.uuid() })
  .meta({ id: 'MarkInternalChatReadRequest' });

export const listInternalChatMentionsQuerySchema = listPerformanceChatMentionsQuerySchema
  .omit({ performanceId: true })
  .extend({ enquiryId: z.uuid() })
  .meta({ id: 'ListInternalChatMentionsQuery' });

/** Hvor mange én forespørsel kan legge til. Et rom å drøfte i, ikke en utsendelsesliste. */
export const MAX_INTERNAL_CHAT_ADDITIONS = 20;

/**
 * Å legge personer til. Fra BYRÅETS kontaktregister — også en manager i et annet selskap står der.
 * Serveren setter hver av dem ned i det huset de kan lese fra, og nekter en som er medlem hos
 * arrangøren (`organizer_person`).
 */
export const addInternalChatParticipantsRequestSchema = z
  .object({
    enquiryId: z.uuid(),
    personIds: z.array(z.uuid()).min(1).max(MAX_INTERNAL_CHAT_ADDITIONS),
  })
  .meta({ id: 'AddInternalChatParticipantsRequest' });

/** 288.1: byrået tar en gjest ut av rommet igjen. Hen mister setet og leser ikke mer. */
export const removeInternalChatGuestRequestSchema = z
  .object({
    enquiryId: z.uuid(),
    personId: z.uuid(),
  })
  .meta({ id: 'RemoveInternalChatGuestRequest' });

/**
 * DET GJESTEN SER AV FORESPØRSELEN (sak 211.3).
 *
 * Artist, arrangør, dato og sted — og svaret bare når det er SENDT (`answeredAt`). Før det er
 * `answer` null, uansett hva byrået har skrevet inn. Porten er `app.internal_thread_summary`, som
 * aldri gir fra seg kolonnene før `answered_at` er satt.
 */
export const threadBookingAnswerDtoSchema = z
  .object({
    answeredAt: z.iso.datetime(),
    availability: enquiryAvailabilitySchema,
    askingFeeMinor: z.number().int().nullable(),
    currency: z.string(),
    holdUntil: z.iso.date().nullable(),
    note: z.string().nullable(),
  })
  .meta({ id: 'ThreadBookingAnswer' });

export const threadBookingSummaryDtoSchema = z
  .object({
    artistName: z.string(),
    organizerName: z.string(),
    performanceDate: z.iso.date().nullable(),
    venueName: z.string().nullable(),
    answer: threadBookingAnswerDtoSchema.nullable(),
  })
  .meta({ id: 'ThreadBookingSummary' });

export type InternalChatDto = z.infer<typeof internalChatDtoSchema>;
export type ListInternalChatMessagesQuery = z.infer<typeof listInternalChatMessagesQuerySchema>;
export type CreateInternalChatMessageRequest = z.infer<
  typeof createInternalChatMessageRequestSchema
>;
export type MarkInternalChatReadRequest = z.infer<typeof markInternalChatReadRequestSchema>;
export type ListInternalChatMentionsQuery = z.infer<typeof listInternalChatMentionsQuerySchema>;
export type AddInternalChatParticipantsRequest = z.infer<
  typeof addInternalChatParticipantsRequestSchema
>;
export type InternalChatGuestDto = z.infer<typeof internalChatGuestDtoSchema>;
export type RemoveInternalChatGuestRequest = z.infer<typeof removeInternalChatGuestRequestSchema>;
export type ThreadBookingAnswerDto = z.infer<typeof threadBookingAnswerDtoSchema>;
export type ThreadBookingSummaryDto = z.infer<typeof threadBookingSummaryDtoSchema>;
